import { memo } from "react"
import { FiSearch } from "react-icons/fi"

import { StudentActionsMenu } from "@/components/layout/Common/StudentActionsMenu"
import type { StudentResponse } from "@/types/student"
import { EmptyState, Flex, Table, VStack } from "@chakra-ui/react"

const formatFieldValue = (value: unknown, type: string): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A"
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "N/A"
  }

  if (type === "bool") {
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return "Yes"
      if (value.toLowerCase() === "false") return "No"
      return value
    }
    return value ? "Yes" : "No"
  }

  if (type === "datetime" || type === "date" || type === "time") {
    const dateValue = new Date(String(value))
    if (!Number.isNaN(dateValue.getTime())) {
      if (type === "date") return dateValue.toLocaleDateString()
      if (type === "time") return dateValue.toLocaleTimeString()
      return dateValue.toLocaleString()
    }
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return "N/A"
    }
  }

  return String(value)
}

interface StudentListProps {
  students: StudentResponse[]
}

const StudentList = memo(({ students }: StudentListProps) => {
  if (!students?.length) {
    return (
      <EmptyState.Root>
        <EmptyState.Content>
          <EmptyState.Indicator>
            <FiSearch />
          </EmptyState.Indicator>
          <VStack textAlign="center">
            <EmptyState.Title>No students yet</EmptyState.Title>
            <EmptyState.Description>
              Create a new student to see it listed here.
            </EmptyState.Description>
          </VStack>
        </EmptyState.Content>
      </EmptyState.Root>
    )
  }

  return (
    <Table.Root size={{ base: "sm", md: "md" }}>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeader w="sm">ID</Table.ColumnHeader>
          <Table.ColumnHeader>First Name</Table.ColumnHeader>
          <Table.ColumnHeader>Last Name</Table.ColumnHeader>
          <Table.ColumnHeader>Date Of Birth</Table.ColumnHeader>
          <Table.ColumnHeader>Gender</Table.ColumnHeader>
          <Table.ColumnHeader>Household Id</Table.ColumnHeader>
          <Table.ColumnHeader>Current Swim Level</Table.ColumnHeader>
          <Table.ColumnHeader>Skill Level Notes</Table.ColumnHeader>
          <Table.ColumnHeader>Has Asthma</Table.ColumnHeader>
          <Table.ColumnHeader>Preferred Location</Table.ColumnHeader>
          <Table.ColumnHeader>Notes</Table.ColumnHeader>
          <Table.ColumnHeader>Is Active</Table.ColumnHeader>
          <Table.ColumnHeader>Photo Url</Table.ColumnHeader>
          <Table.ColumnHeader>Created By</Table.ColumnHeader>
          <Table.ColumnHeader>Created</Table.ColumnHeader>
          <Table.ColumnHeader>Updated By</Table.ColumnHeader>
          <Table.ColumnHeader>Updated</Table.ColumnHeader>
          <Table.ColumnHeader w="xs">Actions</Table.ColumnHeader>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {students.map((student) => (
          <Table.Row key={student.id}>
            <Table.Cell truncate maxW="sm">
              {student.id}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(student.first_name, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(student.last_name, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(student.date_of_birth, "date")}
            </Table.Cell>
            <Table.Cell>{formatFieldValue(student.gender, "str")}</Table.Cell>
            <Table.Cell>
              {formatFieldValue(student.household_id, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(student.current_swim_level, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(student.skill_level_notes, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(student.has_asthma, "bool")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(student.preferred_location, "str")}
            </Table.Cell>
            <Table.Cell>{formatFieldValue(student.notes, "str")}</Table.Cell>
            <Table.Cell>
              {formatFieldValue(student.is_active, "bool")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(student.photo_url, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(student.created_by, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(student.created_at, "datetime")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(student.updated_by, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(student.updated_at, "datetime")}
            </Table.Cell>
            <Table.Cell>
              <Flex justify="flex-end">
                <StudentActionsMenu student={student} />
              </Flex>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )
})

StudentList.displayName = "StudentList"

export default StudentList
